import { useState, useEffect, useRef } from 'react'
import { useIntelData } from '../../contexts/IntelDataContext'
import { Spinner } from '../../components/Spinner'

const TOPICS = ['', 'gold', 'silver', 'usd', 'equities', 'crypto', 'geopolitics', 'macro', 'recession', 'war', 'other']
const OUTCOMES = ['', 'unknown', 'correct', 'incorrect', 'partial']

const directionColors: Record<string, string> = {
  bullish: 'text-emerald-700 bg-emerald-100',
  bearish: 'text-red-700 bg-red-100',
  neutral: 'text-gray-700 bg-gray-100',
  warning: 'text-yellow-700 bg-yellow-100',
  other: 'text-gray-700 bg-gray-100',
}

const outcomeColors: Record<string, string> = {
  correct: 'text-emerald-700 bg-emerald-100',
  incorrect: 'text-red-700 bg-red-100',
  partial: 'text-yellow-700 bg-yellow-100',
  unknown: 'text-gray-600 bg-gray-100',
}

interface Prediction {
  id: number
  statement: string
  influencer_name: string
  prediction_date: string
  direction: string
  topic: string
  asset: string | null
  confidence_pct: number
  timeframe_raw: string | null
  brier_score: number | null
  outcome: string
}

interface Influencer {
  id: number
  name: string
}

interface PredictionEvent {
  id: number
  event_type: string
  content: string
  author: string
  created_at: string
}

interface ContextEntry {
  loading?: boolean
  error?: boolean
  data?: { before: string[]; match: string; after: string[] }
  reason?: string
}

export default function Predictions() {
  const intel = useIntelData()
  const cacheRef = useRef({ cache: intel.predictionsCache, setCache: intel.cachePredictions })
  cacheRef.current = { cache: intel.predictionsCache, setCache: intel.cachePredictions }

  const [predictions, setPredictions] = useState<Prediction[]>((intel.predictionsCache?.data || []) as Prediction[])
  const [influencers, setInfluencers] = useState<Influencer[]>([])
  const [filters, setFilters] = useState({ topic: '', influencer: '', outcome: '' })
  const [viewMode, setViewMode] = useState<'all' | 'evaluated'>('all')
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [events, setEvents] = useState<Record<number, PredictionEvent[]>>({})
  const [feedbackText, setFeedbackText] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [rescoring, setRescoring] = useState<number | null>(null)
  const [contextMap, setContextMap] = useState<Record<number, ContextEntry>>({})
  const [showContextId, setShowContextId] = useState<number | null>(null)
  const [loading, setLoading] = useState(!intel.predictionsCache?.data)

  useEffect(() => {
    fetch('/api/intel/influencers').then(r => r.ok ? r.json() : []).then(setInfluencers).catch(() => {})
  }, [])

  useEffect(() => {
    const { cache, setCache } = cacheRef.current
    const now = Date.now()
    const isStale = !cache || cache.timestamp < now - 30000 || JSON.stringify(cache.filters) !== JSON.stringify(filters)
    if (!isStale) {
      setPredictions(cache.data)
      setLoading(false)
      return
    }
    setLoading(true)
    const params = new URLSearchParams()
    if (filters.topic) params.set('topic', filters.topic)
    if (filters.influencer) params.set('influencer', filters.influencer)
    if (filters.outcome) params.set('outcome', filters.outcome)
    fetch(`/api/intel/predictions?${params}`)
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        const filtered = data.filter(p => p.confidence_pct > 0)
        setPredictions(filtered)
        setCache(filtered, filters)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [filters])

  const refreshPredictions = () => {
    const params = new URLSearchParams()
    if (filters.topic) params.set('topic', filters.topic)
    if (filters.influencer) params.set('influencer', filters.influencer)
    if (filters.outcome) params.set('outcome', filters.outcome)
    fetch(`/api/intel/predictions?${params}`)
      .then(r => r.json())
      .then(data => {
        const filtered = data.filter(p => p.confidence_pct > 0)
        setPredictions(filtered)
        cacheRef.current.setCache(filtered, filters)
      })
      .catch(() => {})
  }

  const setOutcome = async (id, outcome) => {
    const res = await fetch(`/api/intel/predictions/${id}/outcome`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ outcome })
    })
    const data = await res.json()
    setPredictions(prev => prev.map(p => p.id === id ? { ...p, outcome, brier_score: data.brier_score } : p))
    await fetch(`/api/intel/predictions/${id}/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event_type: 'manual_override', content: `Manual override: outcome set to ${outcome}` })
    })
    if (expandedId === id) {
      const evRes = await fetch(`/api/intel/predictions/${id}/events`)
      const evData = await evRes.json()
      setEvents(prev => ({ ...prev, [id]: evData }))
    }
  }

  const toggleExpand = async (id) => {
    if (expandedId === id) { setExpandedId(null); return }
    setExpandedId(id)
    const res = await fetch(`/api/intel/predictions/${id}/events`)
    const data = await res.json()
    setEvents(prev => ({ ...prev, [id]: data }))
  }

  const fetchContext = async (id) => {
    if (showContextId === id) { setShowContextId(null); return }
    setShowContextId(id)
    if (contextMap[id]) return
    setContextMap(prev => ({ ...prev, [id]: { loading: true } }))
    try {
      const res = await fetch(`/api/intel/predictions/${id}/context`)
      const data = await res.json()
      setContextMap(prev => ({ ...prev, [id]: { loading: false, data: data.context, reason: data.reason } }))
    } catch {
      setContextMap(prev => ({ ...prev, [id]: { loading: false, error: true } }))
    }
  }

  const submitFeedback = async (id) => {
    if (!feedbackText.trim()) return
    setSubmitting(true)
    try {
      const res = await fetch(`/api/intel/predictions/${id}/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event_type: 'user_feedback', content: feedbackText })
      })
      const data = await res.json()
      if (data.events) {
        setEvents(prev => ({ ...prev, [id]: data.events }))
      }
      setFeedbackText('')
      refreshPredictions()
    } finally {
      setSubmitting(false)
    }
  }

  const handleRescore = async (id) => {
    setRescoring(id)
    try {
      const res = await fetch(`/api/intel/predictions/${id}/rescore`, { method: 'POST' })
      const data = await res.json()
      if (data.events) {
        setEvents(prev => ({ ...prev, [id]: data.events }))
      }
      refreshPredictions()
    } finally {
      setRescoring(null)
    }
  }

  const evaluated = predictions.filter(p => p.outcome !== 'unknown')
  const pending = predictions.filter(p => p.outcome === 'unknown')
  const displayed = viewMode === 'evaluated' ? evaluated : predictions

  return (
    <div>
      <h2 className="text-xl sm:text-2xl font-bold mb-4 text-gray-900">Predictions</h2>

      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setViewMode('all')}
          className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${
            viewMode === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          All predictions
          <span className="ml-2 text-xs opacity-70">{predictions.length}</span>
        </button>
        <button
          onClick={() => setViewMode('evaluated')}
          className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${
            viewMode === 'evaluated' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Evaluated
          <span className="ml-2 text-xs opacity-70">{evaluated.length}</span>
        </button>
      </div>

      {pending.length > 0 && (
        <div className="mb-4 px-4 py-2.5 rounded-lg bg-yellow-50 text-yellow-800 border border-yellow-200 text-xs">
          <span className="font-medium">{pending.length} predictions pending evaluation</span>
          {' '}&mdash; their time window hasn&apos;t elapsed yet, or no market data is available for scoring.
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        <select
          value={filters.influencer}
          onChange={e => setFilters(f => ({ ...f, influencer: e.target.value }))}
          className="w-full sm:w-auto bg-white border border-gray-300 rounded px-3 py-1.5 text-sm text-gray-900"
        >
          <option value="">All KOLs</option>
          {influencers.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
        </select>
        <select
          value={filters.topic}
          onChange={e => setFilters(f => ({ ...f, topic: e.target.value }))}
          className="w-full sm:w-auto bg-white border border-gray-300 rounded px-3 py-1.5 text-sm text-gray-900"
        >
          {TOPICS.map(t => <option key={t} value={t}>{t || 'All topics'}</option>)}
        </select>
        {viewMode === 'all' && (
          <select
            value={filters.outcome}
            onChange={e => setFilters(f => ({ ...f, outcome: e.target.value }))}
            className="w-full sm:w-auto bg-white border border-gray-300 rounded px-3 py-1.5 text-sm text-gray-900"
          >
            {OUTCOMES.map(o => <option key={o} value={o}>{o || 'All outcomes'}</option>)}
          </select>
        )}
      </div>

      {loading ? (
        <div className="bg-white border border-gray-200 rounded-lg h-[400px] flex flex-col items-center justify-center gap-3">
          <Spinner size={32} />
          <span className="text-sm text-gray-500">Loading predictions...</span>
        </div>
      ) : displayed.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-500">
          {viewMode === 'evaluated' ? 'No evaluated predictions yet.' : 'No predictions found.'}
        </div>
      ) : (
        <div className="space-y-2">
          {displayed.map(p => (
            <div key={p.id} className={`bg-white rounded-lg border ${p.outcome === 'unknown' ? 'border-gray-200' : 'border-gray-200'} shadow-sm`}>
              <div className="p-4">
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900">{p.statement}</p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <span className="text-xs text-gray-500">{p.influencer_name}</span>
                      <span className="text-xs text-gray-400">|</span>
                      <span className="text-xs text-gray-500">{p.prediction_date}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${directionColors[p.direction]}`}>
                        {p.direction}
                      </span>
                      <span className="text-xs bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">
                        {p.topic}{p.asset ? `: ${p.asset}` : ''}
                      </span>
                      <span className="text-xs text-gray-500">{p.confidence_pct}% conf</span>
                      {p.timeframe_raw && (
                        <span className="text-xs text-gray-500">{p.timeframe_raw}</span>
                      )}
                      {p.brier_score != null && (
                        <span className="text-xs text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">
                          Brier {p.brier_score.toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-xs px-2 py-0.5 rounded ${outcomeColors[p.outcome]}`}>
                      {p.outcome}
                    </span>
                    <div className="flex gap-1">
                      {['correct', 'partial', 'incorrect'].map(o => (
                        <button
                          key={o}
                          onClick={() => setOutcome(p.id, o)}
                          className={`text-xs px-2 py-1 rounded transition-colors ${
                            p.outcome === o
                              ? outcomeColors[o]
                              : 'text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200'
                          }`}
                        >
                          {o === 'correct' ? '\u2713' : o === 'incorrect' ? '\u2717' : '~'}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => fetchContext(p.id)}
                      className="text-xs text-gray-500 hover:text-blue-600 px-1.5 py-1 rounded bg-gray-100 hover:bg-gray-200 transition-colors"
                      title="Show transcript context"
                    >
                      {showContextId === p.id ? '\u{1F4C4} \u2715' : '\u{1F4C4}'}
                    </button>
                    <button
                      onClick={() => toggleExpand(p.id)}
                      className="text-xs text-gray-500 hover:text-gray-700 px-1.5 py-1 rounded bg-gray-100 hover:bg-gray-200 transition-colors"
                    >
                      {expandedId === p.id ? '\u25B2' : '\u25BC'}
                    </button>
                  </div>
                </div>

                {showContextId === p.id && (() => {
                  const ctx = contextMap[p.id]
                  if (!ctx || ctx.loading) return <div className="px-4 pb-3 flex items-center gap-2"><Spinner size={14} /><p className="text-xs text-gray-500 italic">Loading context...</p></div>
                  if (ctx.error) return <div className="px-4 pb-3"><p className="text-xs text-red-600">Failed to load context.</p></div>
                  if (!ctx.data) return <div className="px-4 pb-3"><p className="text-xs text-gray-500 italic">Context not available ({ctx.reason === 'no_transcript' ? 'no transcript' : 'statement not found in transcript'}).</p></div>
                  return (
                    <div className="mx-4 mb-3 bg-gray-50 border border-gray-200 rounded p-3 space-y-1">
                      {ctx.data.before.map((s, i) => (
                        <p key={`b${i}`} className="text-xs text-gray-600">...{s}</p>
                      ))}
                      <p className="text-xs text-gray-900 font-bold border-l-2 border-yellow-500 pl-2">{ctx.data.match}</p>
                      {ctx.data.after.map((s, i) => (
                        <p key={`a${i}`} className="text-xs text-gray-600">{s}...</p>
                      ))}
                    </div>
                  )
                })()}
              </div>

              {expandedId === p.id && (
                <div className="border-t border-gray-200 px-4 py-3">
                  <div className="space-y-1.5 mb-3 max-h-64 overflow-y-auto">
                    {(events[p.id] || []).length === 0 ? (
                      <p className="text-xs text-gray-500 italic">No events yet.</p>
                    ) : (events[p.id] || []).map(ev => {
                      const borderColorMap: Record<string, string> = {
                        user_feedback: 'border-blue-500',
                        system_reply: 'border-gray-300',
                        score_change: 'border-yellow-500',
                        manual_override: 'border-purple-500',
                        data_correction: 'border-orange-500',
                      }
                      const borderColor = borderColorMap[ev.event_type] || 'border-gray-300'
                      const isSystem = ev.event_type === 'system_reply'
                      return (
                        <div key={ev.id} className={`border-l-2 ${borderColor} pl-2 py-1`}>
                          <p className={`text-xs ${isSystem ? 'text-gray-500 italic' : 'text-gray-700'}`}>
                            {ev.content}
                          </p>
                          <p className="text-[10px] text-gray-500 mt-0.5">
                            {ev.author} / {ev.event_type} / {ev.created_at}
                          </p>
                        </div>
                      )
                    })}
                  </div>

                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={expandedId === p.id ? feedbackText : ''}
                      onChange={e => setFeedbackText(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && submitFeedback(p.id)}
                      placeholder="Add feedback..."
                      className="flex-1 bg-white border border-gray-300 rounded px-2 py-1 text-xs text-gray-900 placeholder-gray-400"
                    />
                    <button
                      onClick={() => submitFeedback(p.id)}
                      disabled={submitting}
                      className="text-xs px-3 py-1 rounded bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50 transition-colors"
                    >
                      {submitting ? '...' : 'Submit'}
                    </button>
                    <button
                      onClick={() => handleRescore(p.id)}
                      disabled={rescoring === p.id}
                      className="text-xs px-3 py-1 rounded bg-yellow-600 hover:bg-yellow-500 text-white disabled:opacity-50 transition-colors"
                    >
                      {rescoring === p.id ? '...' : 'Re-score'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
